import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';

export const ListView = ({ schema, onEdit, onCreate, onRefresh }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortField, setSortField] = useState(schema.listView?.defaultSort?.field || '_id');
  const [sortOrder, setSortOrder] = useState(schema.listView?.defaultSort?.order || 'asc');

  useEffect(() => {
    loadDocuments();
  }, [schema.modelName]);

  const loadDocuments = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiService.getAll(schema.modelName);
      setDocuments(Array.isArray(data) ? data : data.data || []);
    } catch (err) {
      setError(err.message || 'Failed to load documents');
      console.error('Error loading documents:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) {
      return;
    }

    try {
      await apiService.delete(schema.modelName, id);
      await loadDocuments(); // Reload the list
      if (onRefresh) onRefresh();
    } catch (err) {
      alert(`Error deleting: ${err.message}`);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const formatValue = (value, fieldName) => {
    const fieldDef = schema.fields[fieldName];

    if (value === null || value === undefined) {
      return '-';
    }

    // Handle dates
    if (fieldDef?.type === 'Date' || value instanceof Date) {
      return new Date(value).toLocaleDateString();
    }

    // Handle booleans
    if (typeof value === 'boolean') {
      return value ? '✓' : '✗';
    }

    // Handle arrays
    if (Array.isArray(value)) {
      // If it's an array of references (populated objects with _id from another collection)
      if (value.length > 0 && typeof value[0] === 'object' && value[0]._id && fieldDef?.ref) {
        return value.map(item => getDisplayName(item)).join(', ');
      }
      
      // If it's an array of embedded documents (ArrayOfObjects type)
      if (value.length > 0 && typeof value[0] === 'object' && fieldDef?.type === 'ArrayOfObjects') {
        // Try to show meaningful field values, not _ids
        const displayValues = value.map(item => {
          // Try common meaningful fields
          const meaningfulFields = ['name', 'account', 'title', 'label', 'type', 'game'];
          for (const field of meaningfulFields) {
            if (item[field]) {
              return String(item[field]);
            }
          }
          // If no meaningful field, just show it exists
          return null;
        }).filter(Boolean);
        
        if (displayValues.length > 0) {
          // Show first 3 items, then count if more
          if (displayValues.length <= 3) {
            return displayValues.join(', ');
          } else {
            return `${displayValues.slice(0, 3).join(', ')}, +${displayValues.length - 3} more`;
          }
        }
        
        // No meaningful fields found, just show count
        return `${value.length} items`;
      }
      
      return value.length > 0 ? `${value.length} items` : 'None';
    }

    // Handle objects
    if (typeof value === 'object') {
      // If it's a populated reference (has _id), show the display name
      if (value._id) {
        return getDisplayName(value);
      }
      
      // If it's a discriminator with gameType
      if (fieldDef?.type === 'Discriminator' && value.gameType) {
        return `${value.gameType} config`;
      }
      
      // Generic object
      return 'Object';
    }

    return String(value);
  };

  // Helper to get a display name from a referenced object
  const getDisplayName = (obj) => {
    if (!obj || typeof obj !== 'object') return String(obj);
    
    // Try common name fields in order of preference
    const nameFields = ['name', 'title', 'username', 'email', 'label', 'displayName'];
    for (const field of nameFields) {
      if (obj[field]) {
        return String(obj[field]);
      }
    }
    
    // If we have firstName and lastName, combine them
    if (obj.firstName || obj.lastName) {
      return [obj.firstName, obj.lastName].filter(Boolean).join(' ');
    }
    
    // Fall back to _id
    return obj._id ? String(obj._id) : 'Unknown';
  };

  const sortedDocuments = [...documents].sort((a, b) => {
    const aVal = a[sortField];
    const bVal = b[sortField];
    
    if (aVal === bVal) return 0;
    if (aVal === null || aVal === undefined) return 1;
    if (bVal === null || bVal === undefined) return -1;
    
    const comparison = aVal < bVal ? -1 : 1;
    return sortOrder === 'asc' ? comparison : -comparison;
  });

  if (loading) {
    return <div className="loading">Loading {schema.displayName}...</div>;
  }

  if (error) {
    return (
      <div className="error">
        <p>Error: {error}</p>
        <button onClick={loadDocuments} className="btn btn-primary">Retry</button>
      </div>
    );
  }

  const columns = schema.listView?.columns || Object.keys(schema.fields).slice(0, 5);

  return (
    <div className="list-view">
      <div className="list-header">
        <h2>{schema.displayName}</h2>
        <button onClick={onCreate} className="btn btn-primary">
          Create New {schema.displayName.slice(0, -1)}
        </button>
      </div>

      {documents.length === 0 ? (
        <div className="empty-state">
          <p>No {schema.displayName.toLowerCase()} found.</p>
          <button onClick={onCreate} className="btn btn-primary">
            Create your first {schema.displayName.slice(0, -1).toLowerCase()}
          </button>
        </div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                {columns.map((columnName) => (
                  <th
                    key={columnName}
                    onClick={() => handleSort(columnName)}
                    className={sortField === columnName ? `sorted ${sortOrder}` : ''}
                  >
                    {schema.fields[columnName]?.label || columnName}
                    {sortField === columnName && (
                      <span className="sort-indicator">
                        {sortOrder === 'asc' ? ' ▲' : ' ▼'}
                      </span>
                    )}
                  </th>
                ))}
                <th className="actions-column">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedDocuments.map((doc) => (
                <tr key={doc._id}>
                  {columns.map((columnName) => (
                    <td key={columnName}>
                      {formatValue(doc[columnName], columnName)}
                    </td>
                  ))}
                  <td className="actions-cell">
                    <button
                      onClick={() => onEdit(doc)}
                      className="btn btn-sm btn-secondary"
                      title="Edit"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(doc._id)}
                      className="btn btn-sm btn-danger"
                      title="Delete"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ListView;
